import { useState } from 'react';
import { useStore } from '../store.js';

const REQUIRED = [
  ['gross_monthly_sales', 'Gross monthly sales'],
  ['monthly_cc_volume',   'Monthly credit card volume'],
  ['avg_bank_balance',    'Average bank account balance'],
  ['has_liens',           'Liens'],
  ['previous_mca',        'Previous MCA'],
];

function validateFunding(data) {
  const errors = {};
  for (const [k, label] of REQUIRED) {
    if (!String(data[k] ?? '').trim()) errors[k] = `${label} is required.`;
  }
  for (const k of ['gross_monthly_sales', 'monthly_cc_volume', 'avg_bank_balance']) {
    if (errors[k]) continue;
    const n = Number(String(data[k]).replace(/[^\d.-]/g, ''));
    if (!isFinite(n) || n < 0) errors[k] = 'Must be a valid amount.';
  }
  return errors;
}

function Field({ k, label, type = 'text', required, errors, clearError }) {
  const value = useStore((s) => s.data[k] ?? '');
  const setField = useStore((s) => s.setField);
  const error = errors?.[k];
  return (
    <label className="block">
      <span className="field-label">{label}{required && <span className="text-red-500"> *</span>}</span>
      <input
        type={type}
        className={`field-input ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={value}
        onChange={(e) => {
          setField(k, e.target.value);
          if (error) clearError?.(k);
        }}
      />
      {error && <p className="field-error">{error}</p>}
    </label>
  );
}

function Select({ k, label, options, required, errors, clearError }) {
  const value = useStore((s) => s.data[k] ?? '');
  const setField = useStore((s) => s.setField);
  const error = errors?.[k];
  return (
    <label className="block">
      <span className="field-label">{label}{required && <span className="text-red-500"> *</span>}</span>
      <select
        className={`field-select ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={value}
        onChange={(e) => {
          setField(k, e.target.value);
          if (error) clearError?.(k);
        }}
      >
        <option value="">— select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {error && <p className="field-error">{error}</p>}
    </label>
  );
}

export function FundingInfo({ onNext, onBack }) {
  const data = useStore((s) => s.data);
  const [errors, setErrors] = useState({});
  const clearError = (k) => setErrors((e) => { const { [k]: _, ...rest } = e; return rest; });

  function handleContinue() {
    const errs = validateFunding(data);
    setErrors(errs);
    if (Object.keys(errs).length === 0) onNext();
  }

  const props = { errors, clearError };

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-brand-900">Funding information</h2>
        <p className="text-sm text-slate-500">Help us understand your revenue and history.</p>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field k="gross_monthly_sales" label="Gross monthly sales" required {...props} />
        <Field k="monthly_cc_volume" label="Monthly credit card volume" required {...props} />
        <Field k="avg_bank_balance" label="Average bank account balance" required {...props} />
        <Select k="has_liens" label="Any business liens?" required options={['No', 'Yes']} {...props} />
        <Select k="previous_mca" label="Previous MCA?" required options={['No', 'Yes']} {...props} />
      </div>

      <div className="flex justify-between">
        <button className="btn-ghost" onClick={onBack}>Back</button>
        <button className="btn-primary" onClick={handleContinue}>Continue</button>
      </div>
    </div>
  );
}
